import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormioForm } from '@formio/angular';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { IDropdownOption } from '../../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../../../utilities/http-utilities.service';
import { IWorkInstructionTemplate, TAddWorkInstructionTemplate } from '../../work-instruction-templates/work-instruction-templates.interface';

interface ISaveAsTemplateDialogState {
  createTemplateStatus: ELoadStatus;
  getTemplateDropdownOptionsStatus: ELoadStatus;
  templateDropdownOptions: IDropdownOption[];
  updateTemplateContentStatus: ELoadStatus;
}

@Injectable()
export class SaveAsTemplateDialogStore extends ComponentStore<ISaveAsTemplateDialogState> {
  public readonly templateDropdownOptions = this.selectSignal((state) => state.templateDropdownOptions);

  readonly getTemplateDropdownOptions = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ getTemplateDropdownOptionsStatus: ELoadStatus.loading });

        return this.loadTemplateDropdownOptions(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IWorkInstructionTemplate>) =>
              this.patchState({
                getTemplateDropdownOptionsStatus: ELoadStatus.success,
                templateDropdownOptions: response.data,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly createOneTemplate = this.effect((trigger$: Observable<TAddWorkInstructionTemplate>) =>
    trigger$.pipe(
      switchMap((item: TAddWorkInstructionTemplate) => {
        this.patchState({ createTemplateStatus: ELoadStatus.loading });

        return this.createWorkInstructionTemplate(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ createTemplateStatus: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateTemplateContent = this.effect((trigger$: Observable<{ form: FormioForm; id: number }>) =>
    trigger$.pipe(
      switchMap((updateData) => {
        this.patchState({ updateTemplateContentStatus: ELoadStatus.loading });

        return this.updateOneTemplate(updateData).pipe(
          tapResponse(
            (): void => {
              this.patchState({ updateTemplateContentStatus: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  private readonly httpUtilities: HttpUtilitiesService = inject(HttpUtilitiesService);

  constructor(private readonly http: HttpClient) {
    super({
      createTemplateStatus: ELoadStatus.initial,
      getTemplateDropdownOptionsStatus: ELoadStatus.initial,
      templateDropdownOptions: [],
      updateTemplateContentStatus: ELoadStatus.initial,
    });
  }

  private loadTemplateDropdownOptions(params: HttpParams): Observable<IGetManyResponse<IWorkInstructionTemplate>> {
    return this.http.get<IGetManyResponse<IWorkInstructionTemplate>>('guide-templates', { params });
  }

  private createWorkInstructionTemplate(body: TAddWorkInstructionTemplate): Observable<IBaseResponse<IWorkInstructionTemplate>> {
    return this.http.post<IBaseResponse<IWorkInstructionTemplate>>('guide-templates', body);
  }

  private updateOneTemplate(updateData: { form: FormioForm; id: number }): Observable<IBaseResponse<IWorkInstructionTemplate>> {
    return this.http.patch<IBaseResponse<IWorkInstructionTemplate>>(`guide-templates/${updateData.id}`, {
      form: updateData.form,
    });
  }
}
